// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { Box, Card, CardContent, CardHeader, Link as MuiLink, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Users } from "lucide-react";

import { TimezoneClock } from "@crm-src/components/timezone-clock";
import { InlineSalespeopleEditor, InlineSelectEditor } from "@crm-src/components/inline-edit";
import { formatIndustry, INDUSTRY_OPTIONS } from "@crm-src/lib/industries";

import type {
  DetailedCustomer,
  CustomerBusinessCategory,
} from "@crm-src/types/customer";

const BUSINESS_CATEGORY_OPTIONS: { value: CustomerBusinessCategory; label: string }[] = [
  { value: "service", label: "SERVICE" },
  { value: "equipment_purchaser", label: "EQUIPMENT" },
];

function formatBusinessCategory(businessCategory: CustomerBusinessCategory) {
  switch (businessCategory) {
    case "service":
      return "SERVICE";
    case "equipment_purchaser":
      return "EQUIPMENT";
    default:
      return "EQUIPMENT";
  }
}

// The business-category badge keeps two distinct accents: "service" reads
// purple (mapped to the secondary palette per the converted CRM idiom) and
// "equipment_purchaser" reads orange (mapped to warning).
function getBusinessCategoryColor(businessCategory: CustomerBusinessCategory) {
  return businessCategory === "service" ? "secondary" : "warning";
}

/**
 * The "Overview" tab of the customer detail page — Business Overview +
 * Address. Extracted from the old single-card layout; the customer's
 * identity (avatar / name / id) now lives in the persistent page header,
 * and contacts moved to their own tab.
 */
export function CustomerOverviewTab({
  customer,
  editable = false,
  onAssignSalespeople,
  onChangeBusinessCategory,
  onChangeIndustry,
}: {
  customer: DetailedCustomer;
  /**
   * When true (and the matching handler is supplied), the Salesperson and
   * Business Type rows become inline-editable in place — same primitives as the
   * list page, gated by the caller (super_admin). Defaults to read-only so
   * existing render-only callers and tests are unaffected.
   * See docs/crm.md → "Inline editing".
   */
  editable?: boolean;
  onAssignSalespeople?: (userIds: string[]) => Promise<unknown>;
  onChangeBusinessCategory?: (value: CustomerBusinessCategory) => Promise<unknown>;
  onChangeIndustry?: (value: string) => Promise<unknown>;
}) {
  const businessCategory = customer.business_category || "equipment_purchaser";
  const categoryColor = getBusinessCategoryColor(businessCategory);
  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Users size={20} />
            <Typography component="span" sx={{ fontWeight: 600 }}>
              Customer Information
            </Typography>
          </Box>
        }
      />
      <CardContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Business Overview */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography component="h4" sx={{ fontWeight: 500, fontSize: 14, color: "text.primary" }}>
              Business Overview
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {/* Salesperson */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                  Salesperson
                </Typography>
                <Box>
                  <InlineSalespeopleEditor
                    canEdit={editable && Boolean(onAssignSalespeople)}
                    value={(customer.assigned_salespeople ?? []).map((s) => s.user_id)}
                    onSave={(ids) => onAssignSalespeople!(ids)}
                    variant="panel"
                  >
                    {customer.assigned_salespeople &&
                    customer.assigned_salespeople.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {customer.assigned_salespeople.map((salesperson) => (
                          <Box
                            key={salesperson.id}
                            component="span"
                            sx={{
                              fontSize: 12,
                              fontWeight: 500,
                              color: "text.primary",
                              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            {salesperson.full_name}
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.disabled" }}>
                        Not assigned
                      </Typography>
                    )}
                  </InlineSalespeopleEditor>
                </Box>
              </Box>

              {/* Business Category */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                  Business Type
                </Typography>
                <InlineSelectEditor
                  canEdit={editable && Boolean(onChangeBusinessCategory)}
                  value={businessCategory}
                  options={BUSINESS_CATEGORY_OPTIONS}
                  onSave={(v) => onChangeBusinessCategory!(v as CustomerBusinessCategory)}
                  ariaLabel="Edit business type"
                  errorMessage="Failed to update business type"
                  variant="panel"
                >
                  <Box
                    component="span"
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: (t) => alpha(t.palette[categoryColor].main, 0.16),
                      color: `${categoryColor}.dark`,
                    }}
                  >
                    {formatBusinessCategory(businessCategory)}
                  </Box>
                </InlineSelectEditor>
              </Box>

              {/* Industry */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                  Industry
                </Typography>
                <InlineSelectEditor
                  canEdit={editable && Boolean(onChangeIndustry)}
                  value={customer.industry ?? ""}
                  options={INDUSTRY_OPTIONS}
                  onSave={(v) => onChangeIndustry!(v)}
                  ariaLabel="Edit industry"
                  errorMessage="Failed to update industry"
                  variant="panel"
                >
                  {customer.industry ? (
                    <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
                      {formatIndustry(customer.industry)}
                    </Typography>
                  ) : (
                    <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.disabled" }}>
                      Not set
                    </Typography>
                  )}
                </InlineSelectEditor>
              </Box>
            </Box>
          </Box>

          <Box sx={{ borderTop: 1, borderColor: "divider" }} />

          {/* Address Information */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography component="h4" sx={{ fontWeight: 500, fontSize: 14, color: "text.primary" }}>
              Address Information
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {/* Full Address - Combined */}
              {customer.address ||
              customer.city ||
              customer.state ||
              customer.postal_code ||
              customer.country ? (
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                    Address
                  </Typography>
                  <Box sx={{ fontSize: 14, fontWeight: 500, color: "text.primary", textAlign: "right", maxWidth: 200 }}>
                    {[
                      customer.address,
                      customer.city,
                      customer.state,
                      customer.postal_code,
                      customer.country,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ bgcolor: "surfaceSoft", borderRadius: 1, p: 2, fontSize: 14, color: "text.secondary" }}>
                  Address not provided
                </Box>
              )}

              {/* Website & Timezone - Combined */}
              {(customer.website || customer.timezone) && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {/* Website */}
                  {customer.website && (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                        Website
                      </Typography>
                      <MuiLink
                        href={
                          customer.website.startsWith("http")
                            ? customer.website
                            : `https://${customer.website}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: "primary.main",
                          textDecoration: "underline",
                          "&:hover": { color: "primary.dark" },
                        }}
                      >
                        {customer.website}
                      </MuiLink>
                    </Box>
                  )}

                  {/* Timezone */}
                  {customer.timezone && (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                        Timezone
                      </Typography>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
                          {customer.timezone}
                        </Typography>
                        <TimezoneClock timezone={customer.timezone} />
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
